<template>
  <div class="mem-screen">

    <!-- Header -->
    <div class="mem-header-card">
      <div class="mem-header-top">
        <span class="mem-header-emoji">🃏</span>
        <p class="mem-point-label">{{ t.title }}</p>
      </div>
      <p class="mem-instructions">{{ t.instructions }}</p>
    </div>

    <!-- Stats -->
    <div class="mem-stats">
      <span class="mem-stat">🔄 {{ moves }} {{ t.moves }}</span>
      <span class="mem-stat">✅ {{ matchedCount }} / {{ totalPairs }} {{ t.pairs }}</span>
    </div>

    <!-- Grid -->
    <div class="mem-grid">
      <div
        v-for="card in cards"
        :key="card.id"
        class="mem-card"
        :class="{ flipped: card.flipped || card.matched, matched: card.matched }"
        @click="flip(card)"
      >
        <div class="mem-card-inner">
          <div class="mem-card-back">❓</div>
          <div class="mem-card-front">
            <img :src="card.image" alt="" />
          </div>
        </div>
      </div>
    </div>

    <!-- Victory overlay -->
    <transition name="fade">
      <div v-if="won" class="mem-victory">
        <div class="mem-victory-box">
          <div class="mem-victory-emoji">🎉</div>
          <h2>{{ t.bravo }}</h2>
          <p>{{ t.result(moves) }}</p>
          <p class="mem-score">+{{ score }} {{ t.pts }}</p>
          <button class="mem-continue-btn" @click="done">{{ t.continue }}</button>
        </div>
      </div>
    </transition>

  </div>
</template>

<script>
export default {
  name: 'Memory',

  props: {
    customImages: { type: Array, default: () => [] },
  },

  emits: ['memory-done', 'star-up'],

  data() {
    return {
      cards: [],
      flippedCards: [],
      moves: 0,
      matchedCount: 0,
      totalPairs: 0,
      won: false,
      locked: false,
      correctAnswerSound: require("../../static/sounds/correctAnswer.mp3"),
      wrongAnswerSound: require("../../static/sounds/wrongAnswer.mp3"),
    };
  },

  computed: {
    score() {
      return Math.max(5, 20 - Math.max(0, this.moves - this.totalPairs));
    },

    t() {
      const lang = this.$store.getters.lang || 'fr';
      const translations = {
        fr: {
          title: 'Memory',
          instructions: 'Retrouve toutes les paires !',
          moves: 'coups',
          pairs: 'paires',
          bravo: 'Bravo !',
          result: (moves) => `${moves} coups pour trouver toutes les paires`,
          pts: 'pts',
          continue: 'Continuer',
        },
        he: {
          title: 'זיכרון',
          instructions: 'מצא את כל הזוגות!',
          moves: 'מהלכים',
          pairs: 'זוגות',
          bravo: '!כל הכבוד',
          result: (moves) => `${moves} מהלכים למצוא את כל הזוגות`,
          pts: 'נקודות',
          continue: 'המשך',
        },
        en: {
          title: 'Memory',
          instructions: 'Find all the pairs!',
          moves: 'moves',
          pairs: 'pairs',
          bravo: 'Well done!',
          result: (moves) => `${moves} moves to find all pairs`,
          pts: 'pts',
          continue: 'Continue',
        },
        de: {
          title: 'Memory',
          instructions: 'Finde alle Paare!',
          moves: 'Züge',
          pairs: 'Paare',
          bravo: 'Gut gemacht!',
          result: (moves) => `${moves} Züge um alle Paare zu finden`,
          pts: 'Pkt',
          continue: 'Weiter',
        },
        ar: {
          title: 'الذاكرة',
          instructions: 'ابحث عن جميع الأزواج!',
          moves: 'حركات',
          pairs: 'أزواج',
          bravo: '!أحسنت',
          result: (moves) => `${moves} حركات للعثور على جميع الأزواج`,
          pts: 'نقاط',
          continue: 'متابعة',
        },
      };
      return translations[lang] || translations.fr;
    },
  },

  mounted() {
    this.initGame();
  },

  methods: {
    initGame() {
      const allImages = (() => {
        // Use custom images if provided (uploaded via admin)
        if (this.customImages && this.customImages.length > 0) return this.customImages;
        try {
          const ls = localStorage.getItem('arrayOfImagesBase64');
          if (ls) return JSON.parse(ls);
        } catch (e) { /* ignore */ }
        return this.$store.getters.arrayOfImagesBase64 || [];
      })();

      const valid = allImages.filter(img => img && (img.startsWith('data:') || img.startsWith('http')));

      // Take up to 6 images; if fewer, repeat from start
      const pool = [];
      for (let i = 0; i < 6; i++) {
        if (valid.length === 0) break;
        pool.push(valid[i % valid.length]);
      }

      this.totalPairs = pool.length;

      // Create pairs
      const deck = [];
      pool.forEach((img, idx) => {
        deck.push({ id: idx * 2,     pairId: idx, image: img, flipped: false, matched: false });
        deck.push({ id: idx * 2 + 1, pairId: idx, image: img, flipped: false, matched: false });
      });

      // Shuffle
      for (let i = deck.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [deck[i], deck[j]] = [deck[j], deck[i]];
      }

      this.cards = deck;
      this.flippedCards = [];
      this.moves = 0;
      this.matchedCount = 0;
      this.won = false;
      this.locked = false;
    },

    flip(card) {
      if (this.locked) return;
      if (card.flipped || card.matched) return;
      if (this.flippedCards.length >= 2) return;

      card.flipped = true;
      this.flippedCards.push(card);

      if (this.flippedCards.length === 2) {
        this.moves++;
        this.checkMatch();
      }
    },

    checkMatch() {
      const [a, b] = this.flippedCards;
      if (a.pairId === b.pairId) {
        // Match!
        new Audio(this.correctAnswerSound).play();
        a.matched = true;
        b.matched = true;
        this.matchedCount++;
        this.flippedCards = [];
        if (this.matchedCount === this.totalPairs) {
          setTimeout(() => { this.won = true; }, 400);
        }
      } else {
        // No match — flip back after delay
        new Audio(this.wrongAnswerSound).play();
        this.locked = true;
        setTimeout(() => {
          a.flipped = false;
          b.flipped = false;
          this.flippedCards = [];
          this.locked = false;
        }, 1000);
      }
    },

    done() {
      this.$emit('star-up', this.score);
      this.$emit('memory-done');
    },
  },
};
</script>

<style scoped>
.mem-screen {
  width: 100%;
  max-width: 590px;
  margin: 0 auto;
  padding: 16px 12px 80px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

/* Header */
.mem-header-card {
  width: 100%;
  background: white;
  border-radius: 16px;
  padding: 16px 20px;
  text-align: center;
  box-shadow: 0 2px 10px rgba(0,0,0,0.08);
}
.mem-header-top {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 6px;
}
.mem-header-emoji { font-size: 28px; }
.mem-point-label { font-size: 18px; font-weight: 700; margin: 0; }
.mem-instructions { font-size: 14px; color: #555; margin: 0; }

/* Stats */
.mem-stats {
  display: flex;
  gap: 20px;
}
.mem-stat {
  background: white;
  border-radius: 20px;
  padding: 6px 16px;
  font-size: 14px;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
}

/* Grid */
.mem-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  width: 100%;
}

/* Card */
.mem-card {
  aspect-ratio: 1;
  perspective: 600px;
  cursor: pointer;
}
.mem-card-inner {
  width: 100%;
  height: 100%;
  position: relative;
  transform-style: preserve-3d;
  transition: transform 0.45s ease;
  border-radius: 12px;
}
.mem-card.flipped .mem-card-inner,
.mem-card.matched .mem-card-inner {
  transform: rotateY(180deg);
}
.mem-card-back,
.mem-card-front {
  position: absolute;
  inset: 0;
  border-radius: 12px;
  backface-visibility: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}
.mem-card-back {
  background: linear-gradient(135deg, #1a73e8, #273d5f);
  font-size: 28px;
  box-shadow: 0 3px 10px rgba(0,0,0,0.15);
}
.mem-card-front {
  transform: rotateY(180deg);
  background: white;
  overflow: hidden;
  box-shadow: 0 3px 10px rgba(0,0,0,0.15);
}
.mem-card-front img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 12px;
}
.mem-card.matched .mem-card-front {
  border: 3px solid #34a853;
}

/* Victory */
.mem-victory {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 200;
}
.mem-victory-box {
  background: white;
  border-radius: 20px;
  padding: 36px 32px;
  text-align: center;
  max-width: 300px;
  width: 90%;
}
.mem-victory-emoji { font-size: 52px; margin-bottom: 8px; }
.mem-victory-box h2 { font-size: 26px; font-weight: 800; margin: 0 0 8px; }
.mem-victory-box p { color: #555; margin: 4px 0; }
.mem-score { font-size: 22px; font-weight: 800; color: #1a73e8; margin-top: 10px !important; }
.mem-continue-btn {
  margin-top: 20px;
  background: #1a73e8;
  color: white;
  border: none;
  border-radius: 12px;
  padding: 12px 32px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  width: 100%;
}
.mem-continue-btn:hover { background: #1558b0; }

/* Transition */
.fade-enter-active, .fade-leave-active { transition: opacity 0.3s; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
</style>
